import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, CircularProgress, Alert, Chip, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, IconButton, Tooltip,
  FormControlLabel, Switch,
} from '@mui/material';
import {
  Hub as HubIcon, Refresh as RefreshIcon, Add as AddIcon, Delete as DeleteIcon,
  Edit as EditIcon, WifiTethering as TestIcon,
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const TYPES = [
  { v: 'agent', t: 'Свой агент (push с ПК)' },
  { v: 'glpi', t: 'GLPI (REST API)' },
  { v: 'zabbix', t: 'Zabbix (API)' },
  { v: 'snmp', t: 'SNMP (опрос устройств)' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.v, t.t]));
const STAT = { ok: { c: 'success', t: 'OK' }, error: { c: 'error', t: 'Ошибка' } };

const BLANK = {
  type: 'agent', name: '', enabled: true, base_url: '', username: '', credential: '',
  interval_sec: 3600, app_token: '', snmp_version: '2c', snmp_port: 161,
};

function Integrations() {
  const { user } = useAuth();
  const canManage = user?.is_superuser || user?.permissions?.includes('integrations.manage');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const r = await axios.get('/integrations/'); setRows(r.data || []); }
    catch (e) { setError(e.response?.data?.detail || e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(BLANK); setEditing(null); setDlg(true); };
  const openEdit = (s) => {
    const cfg = s.config_json || {};
    setForm({ type: s.type, name: s.name, enabled: s.enabled, base_url: s.base_url || '',
      username: s.username || '', credential: '', interval_sec: s.interval_sec || 3600,
      app_token: cfg.app_token || '', snmp_version: cfg.version || '2c', snmp_port: cfg.port || 161 });
    setEditing(s); setDlg(true);
  };

  const save = async () => {
    try {
      const config_json = {};
      if (form.type === 'glpi') config_json.app_token = form.app_token;
      if (form.type === 'snmp') { config_json.version = form.snmp_version; config_json.port = Number(form.snmp_port) || 161; }
      const body = { type: form.type, name: form.name, enabled: form.enabled,
        base_url: form.base_url || null, username: form.username || null,
        interval_sec: Number(form.interval_sec) || 3600, config_json };
      if (form.credential) body.credential = form.credential;   // write-only; blank keeps existing
      if (editing) await axios.put(`/integrations/${editing.id}`, body);
      else await axios.post('/integrations/', body);
      setDlg(false); await load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const test = async (s) => {
    try { const r = await axios.post(`/integrations/${s.id}/test`); await load();
      setError(''); window.alert(`${r.data.status === 'ok' ? '✓' : '✗'} ${r.data.detail}`); }
    catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  const del = async (s) => { try { await axios.delete(`/integrations/${s.id}`); await load(); } catch (e) { setError(e.response?.data?.detail || e.message); } };

  const t = form.type;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <HubIcon color="primary" />
        <Typography variant="h4">Интеграции (источники данных)</Typography>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Подключения к внешним системам, из которых двойник получает реальность: свой агент на ПК, GLPI, Zabbix, SNMP.
        Настройте источник здесь один раз, затем на карточке устройства укажите, каким источником его наблюдать.
        Пароли/токены хранятся в зашифрованном виде и не показываются обратно.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction="row" sx={{ mb: 2 }} alignItems="center">
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<RefreshIcon />} sx={{ mr: 1 }} onClick={load}>Обновить</Button>
        {canManage && <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Добавить источник</Button>}
      </Stack>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        : rows.length === 0 ? <Alert severity="info">Источников нет. Добавьте первый — например, «Свой агент» или GLPI.</Alert>
        : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Название</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Тип</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Адрес</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Действия</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map((s) => {
                  const st = STAT[s.last_status] || (s.last_status ? { c: 'default', t: s.last_status } : null);
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.name}{!s.enabled && <Chip size="small" sx={{ ml: 1 }} label="выкл" />}</TableCell>
                      <TableCell>{TYPE_LABEL[s.type] || s.type}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{s.base_url || '—'}</TableCell>
                      <TableCell>
                        {st && <Tooltip title={s.last_detail || ''}><Chip size="small" color={st.c} label={st.t} /></Tooltip>}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {canManage && <Tooltip title="Проверить связь"><IconButton size="small" onClick={() => test(s)}><TestIcon fontSize="small" /></IconButton></Tooltip>}
                        {canManage && <Tooltip title="Изменить"><IconButton size="small" onClick={() => openEdit(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                        {canManage && <Tooltip title="Удалить"><IconButton size="small" color="error" onClick={() => del(s)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      <Dialog open={dlg} onClose={() => setDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Изменить источник' : 'Новый источник данных'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Тип источника" value={t} disabled={!!editing}
              onChange={(e) => setForm({ ...form, type: e.target.value })} fullWidth>
              {TYPES.map((x) => <MenuItem key={x.v} value={x.v}>{x.t}</MenuItem>)}
            </TextField>
            <TextField label="Название" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />

            {(t === 'glpi' || t === 'zabbix') && (
              <TextField label={t === 'glpi' ? 'URL API (…/apirest.php)' : 'URL API (…/api_jsonrpc.php)'}
                value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} fullWidth />
            )}
            {t === 'zabbix' && (
              <TextField label="Пользователь (если по логину/паролю)" value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })} fullWidth size="small" />
            )}
            {t !== 'agent' && (
              <TextField type="password" label={t === 'snmp' ? 'SNMP community' : (t === 'glpi' ? 'User token' : 'API токен / пароль')}
                value={form.credential} placeholder={editing ? '•••• оставьте пустым, чтобы не менять' : ''}
                onChange={(e) => setForm({ ...form, credential: e.target.value })} fullWidth />
            )}
            {t === 'glpi' && (
              <TextField type="password" label="App token (GLPI)" value={form.app_token}
                onChange={(e) => setForm({ ...form, app_token: e.target.value })} fullWidth />
            )}
            {t === 'snmp' && (
              <Stack direction="row" spacing={2}>
                <TextField select label="Версия" value={form.snmp_version} sx={{ width: 120 }}
                  onChange={(e) => setForm({ ...form, snmp_version: e.target.value })}>
                  <MenuItem value="1">v1</MenuItem><MenuItem value="2c">v2c</MenuItem>
                </TextField>
                <TextField type="number" label="Порт" value={form.snmp_port} sx={{ width: 120 }}
                  onChange={(e) => setForm({ ...form, snmp_port: e.target.value })} />
              </Stack>
            )}
            {t === 'agent' && (
              <Alert severity="info" variant="outlined">Агент сам отправляет данные на сервер по токену устройства (enrollment — на карточке устройства). Здесь источник нужен как «канал», адрес/пароль не требуются.</Alert>
            )}
            <TextField type="number" label="Интервал синхронизации, сек" value={form.interval_sec}
              onChange={(e) => setForm({ ...form, interval_sec: e.target.value })} sx={{ width: 240 }} />
            <FormControlLabel control={<Switch checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />} label="Включён" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>Отмена</Button>
          <Button variant="contained" onClick={save} disabled={!form.name}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Integrations;
